/**
 * DECISIONS 403: a family tree's own shapes — its prototype's fitted wood, crown and forms (constants/treeWood.ts,
 * treeFoliage.ts, treeFormShapes.ts) under its per-tree shape draw (core/treeDraws.ts `treeShapeDraw`), as the stands'
 * vertex shader draws them. The walk obstacles (core/surfaceWalk.ts), the crowns (core/foliage.ts) and the limbs a
 * blow sheds (core/debris.ts) read these, so what blocks and hides follows what is drawn. A type with no draw (no
 * family: the classic trees, the log, the shrub) or a variant with no fitted crown keeps its prototype's shapes.
 * Pure: model units in, model units out.
 */
import type { FloraState } from '../types';
import type { TreeWoodVariant, WoodPiece } from '../constants/treeWood';
import type { FoliageBlob } from '../constants/treeFoliage';
import { treeShapeDraw, treeShapePoint, type TreeShapeDraw } from './treeDraws';

type Tree = Pick<FloraState, 'id' | 'floraType'>;
const A = [0, 0, 0, 0], B = [0, 0, 0, 0];

/** The tree's draw against its variant's crown, or null (it stands as its prototype). */
function drawOf(f: Tree, v: TreeWoodVariant | undefined): { d: TreeShapeDraw; crown: readonly [number, number] } | null {
  const d = v?.crown ? treeShapeDraw(f.floraType, f.id) : null;
  return d ? { d, crown: v!.crown! } : null;
}

/**
 * Wood pieces (or a crown's cones) of the tree's variant `v`, drawn: each end moved as the drawing moves it, each radius
 * widened as its crown is where it stands in it. `lying`: a fallen form's pieces, in its lying frame (the variant's).
 */
export function drawnPieces(f: Tree, v: TreeWoodVariant | undefined, pieces: readonly WoodPiece[], lying = false): readonly WoodPiece[] {
  const x = drawOf(f, v), ly = lying ? v?.lying : undefined;
  if (!x || (lying && !ly)) return pieces;
  const { d, crown } = x;
  return pieces.map(p => {
    treeShapePoint(d, crown, p[0], p[1], p[2], ly, A);
    treeShapePoint(d, crown, p[4], p[5], p[6], ly, B);
    return [A[0], A[1], A[2], p[3] * (1 + (d.crownW - 1) * A[3]), B[0], B[1], B[2], p[7] * (1 + (d.crownW - 1) * B[3])] as const;
  });
}

/** A crown's blobs of the tree's variant `v`, drawn: each centre moved, each radius scaled by the crown's mean draw where it stands. */
export function drawnBlobs(f: Tree, v: TreeWoodVariant | undefined, blobs: readonly FoliageBlob[]): readonly FoliageBlob[] {
  const x = drawOf(f, v);
  if (!x) return blobs;
  const { d, crown } = x, s = (d.crownW + d.crownH) / 2 - 1;
  return blobs.map(b => {
    treeShapePoint(d, crown, b[0], b[1], b[2], undefined, A);
    return [A[0], A[1], A[2], b[3] * (1 + s * A[3])] as const;
  });
}

/** The tree's drawn height over its prototype's (×): its top, the variant's height up the trunk, under the draw. */
export function drawnHeightScale(f: Tree, v: TreeWoodVariant | undefined): number {
  const x = drawOf(f, v);
  if (!x || !(v!.height > 0)) return 1;
  return treeShapePoint(x.d, x.crown, 0, v!.height, 0, undefined, A)[1] / v!.height;
}

/** The variant as the tree stands it: its standing wood, its stump and its fallen trunk drawn (its height and frames its prototype's, as the drawing sinks it). */
export function drawnWoodVariant(f: Tree, v: TreeWoodVariant): TreeWoodVariant {
  if (!drawOf(f, v)) return v;
  const kept = drawnKept.get(f);
  if (kept && kept.v === v && kept.id === f.id) return kept.out;
  const out = {
    ...v, whole: drawnPieces(f, v, v.whole),
    ...(v.stump ? { stump: drawnPieces(f, v, v.stump) } : {}),
    ...(v.fallen ? { fallen: drawnPieces(f, v, v.fallen, true) } : {}),
  };
  drawnKept.set(f, { v, id: f.id, out });
  return out;
}
/** Each tree's drawn variant as last made (its entry, its variant and its id the same: the same shapes). */
const drawnKept = new WeakMap<Tree, { v: TreeWoodVariant; id: number; out: TreeWoodVariant }>();
